import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { Choices } from './Choices'
import { ContactFormFields, InsuranceHolder } from './ContactFormFields'
import { GapFillerQueryParams } from './GapFiller'
import { Gender, QueryParams } from './Generic'
import { AllowedInsuranceTypes } from './Insurance'
import { RecallQueryParams } from './Recall'
import { CreationEvent, SourceWebsiteType } from '../lib'
import { InsuranceTypeOptions } from 'src/config/insuranceTypeOptions'
import { PainDetailsData } from '@dentalux/ui-library-core/custom/components/pain-details-form'

interface BookingInfo extends Omit<Choices, 'preferredPractitionerReferenceIds'>, ContactFormFields {
  preferredPractitionerReferenceId?: string
  creationSource: SourceWebsiteType
}

export interface RecallBookingInfo {
  treatmentBundleTypeReferenceId: string
  customerId: string
  start: string
  preferredPractitionerReferenceId: string
  creationSource: SourceWebsiteType
  creationEvent: CreationEvent
}

export interface Booking {
  booking?: Partial<BookingInfo> | RecallBookingInfo
  trackingParams?: RecallQueryParams
  clinicId: string
  lang: string
}

type Correspondence = {
  email?: string
  phoneNumber?: string
}

type PersonData = {
  firstName?: string
  lastName?: string
  gender?: string
}

interface Person {
  correspondence: Correspondence
  data: PersonData
}

interface Patient extends Omit<Person, 'correspondence'> {
  child?: boolean
  birthdate?: string
  insuranceType?: AllowedInsuranceTypes
  correspondence?: Correspondence
}
export interface BookingData
  extends Omit<Choices, 'insuranceType' | 'selectedBookableDateAndTime' | 'preferredPractitionerReferenceIds'> {
  agbAccepted: boolean
  dentalTosAccepted: boolean
  insuranceHolder?: InsuranceHolder
  contactPerson?: Person | null
  patient: Patient
  start: string
  reasonForVisit?: string
  preferredPractitionerReferenceId?: string
  creationSource: SourceWebsiteType
  phoneVerificationCode?: string
  creationEvent: string
  additionalAppointmentHints?: AdditionalAppointmentHints
}

/**
 * This will be renamed to booking when we migrate recalls
 * to v2
 */
export interface BookingV2 {
  booking?: Partial<BookingData>
  trackingParams?: QueryParams
  clinicId: string
  lang: string
}

export type GapFillerBooking = {
  appointmentBundleReferenceId: string
  start: string
  trackingParams?: GapFillerQueryParams
  practitionerId?: string
  creationSource: SourceWebsiteType
  creationEvent: string
}

type PainInformationItem = {
  questionContentfulKey: string
  answerContentfulKey: string
  answerValue: string
}

export type AdditionalAppointmentHints = {
  painInformation?: Partial<{ [K in keyof PainDetailsData]: PainInformationItem }>
  upsellInformation?: typeof vitaminDUpsellInformation
}

export const vitaminDUpsellInformation = {
  vitaminD: {
    questionContentfulKey: 'upsell.vitamin_d.hint.question',
    answerContentfulKey: 'upsell.vitamin_d.hint.answer_yes',
    answerValue: null,
  },
}

export interface ExistingPatientBooking {
  clinicReferenceId: string
  employeeReferenceId?: string
  patientReferenceId: string
  treatmentBundleReferenceId: string
  start: string
  dataPrivacyAccepted?: boolean
  creationSource: SourceWebsiteType
  creationEvent: string
  additionalAppointmentHints?: AdditionalAppointmentHints
}

export interface Appointment {
  clinicReferenceId: string
  employeeReferenceId?: string
  treatmentBundleReferenceId?: string
  start?: string
  email?: string
  phoneNumber?: string
  patient?: AppointmentPatient
  contactPerson?: AppointmentPatient
  patientType?: PatientTypeOptions
  patientUnder18Years?: boolean
  dataPrivacyAccepted?: boolean
  creationSource?: SourceWebsiteType
  phoneVerificationCode?: string
  insuranceHolder?: InsuranceHolder
  insuranceType?: InsuranceTypeOptions
  creationEvent?: string
  additionalAppointmentHints?: AdditionalAppointmentHints
}

export interface AppointmentPatient {
  gender?: Gender
  firstName?: string
  lastName?: string
  birthdate?: string
}

export interface CreateAppointmentForExistingPatientPayload extends ExistingPatientBooking {
  additionalAppointmentHints: AdditionalAppointmentHints | undefined
  trackingParams: QueryParams | undefined
  lang: string
}

export interface SharedBookingResponse {
  anamnesisLink?: string
  healthAssistantLink?: string
  hasHealthAssistant?: boolean
  hasValidAnamnesis?: boolean
}

export interface NewPatientBookingResponse extends SharedBookingResponse {
  appointments: Array<{ referenceId: string }>
  clinicReferenceId: string
  referenceId: string
  start: string
}

export interface ExistingPatientBookingResponse extends SharedBookingResponse {
  referenceId: string
  clinicReferenceId: string
  start?: string
  appointments: Array<{
    referenceId: string
    employeeReferenceId: string
    start: string
    end: string
  }>
}

export interface GapFillerResponse extends SharedBookingResponse {}
